import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { Clock, CheckCircle, XCircle, AlertCircle, Play, Pause } from 'lucide-react';

const MCQAssessment = ({ simulatorState }) => {
  const [selectedCategory, setSelectedCategory] = useState('testing-fundamentals');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [assessmentMode, setAssessmentMode] = useState('practice'); // practice, timed, adaptive
  const [score, setScore] = useState(0);
  const [questionsAttempted, setQuestionsAttempted] = useState(0);

  const categories = [
    {
      id: 'testing-fundamentals',
      name: 'Testing Fundamentals',
      icon: '📚',
      questions: 156,
      difficulty: 'Beginner',
      avgTime: '2.5 min',
      description: 'Core testing concepts and principles'
    },
    {
      id: 'automation-frameworks',
      name: 'Automation Frameworks',
      icon: '🤖',
      questions: 189,
      difficulty: 'Intermediate',
      avgTime: '3.2 min',
      description: 'Selenium, TestNG, Cypress frameworks'
    },
    {
      id: 'api-testing',
      name: 'API Testing',
      icon: '🔌',
      questions: 143,
      difficulty: 'Intermediate',
      avgTime: '2.8 min',
      description: 'REST, GraphQL, Postman, Newman'
    },
    {
      id: 'performance-testing',
      name: 'Performance Testing',
      icon: '⚡',
      questions: 97,
      difficulty: 'Advanced',
      avgTime: '4.1 min',
      description: 'JMeter, LoadRunner, stress testing'
    },
    {
      id: 'security-testing',
      name: 'Security Testing',
      icon: '🔒',
      questions: 78,
      difficulty: 'Advanced',
      avgTime: '3.8 min',
      description: 'OWASP, penetration testing, vulnerabilities'
    },
    {
      id: 'test-management',
      name: 'Test Management',
      icon: '📊',
      questions: 112,
      difficulty: 'Expert',
      avgTime: '3.5 min',
      description: 'Test planning, strategies, methodologies'
    }
  ];

  const sampleQuestions = [
    {
      id: 1,
      category: 'testing-fundamentals',
      question: 'What is the primary difference between black box and white box testing?',
      options: [
        'Black box tests functionality, white box tests structure',
        'Black box is automated, white box is manual',
        'Black box is for unit tests, white box is for integration tests',
        'Black box uses GUI, white box uses CLI'
      ],
      correctAnswer: 0,
      explanation: 'Black box testing focuses on functionality without knowledge of internal structure, while white box testing examines the internal code structure and logic paths.',
      difficulty: 'Easy',
      tags: ['fundamentals', 'testing-types']
    },
    {
      id: 2,
      category: 'automation-frameworks',
      question: 'Which annotation in TestNG is used to execute a method before each test method?',
      options: [
        '@BeforeClass',
        '@BeforeMethod',
        '@BeforeTest',
        '@BeforeSuite'
      ],
      correctAnswer: 1,
      explanation: '@BeforeMethod annotation ensures the annotated method runs before each @Test method in the class, making it ideal for test setup operations.',
      difficulty: 'Medium',
      tags: ['testng', 'annotations', 'automation']
    }
  ];

  const assessmentModes = [
    {
      id: 'practice',
      name: 'Practice Mode',
      description: 'No time limits, instant feedback',
      icon: '🎯'
    },
    {
      id: 'timed',
      name: 'Timed Assessment',
      description: '10-30 minutes, realistic exam conditions',
      icon: '⏱️'
    },
    {
      id: 'adaptive',
      name: 'Adaptive Testing',
      description: 'AI-powered difficulty adjustment',
      icon: '🧠'
    }
  ];

  useEffect(() => {
    let interval = null;
    if (isTimerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((time) => time - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      setIsTimerActive(false);
      handleSubmitAnswer(); // Auto-submit when time runs out
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const startAssessment = (mode = 'practice') => {
    setAssessmentMode(mode);
    setIsTimerActive(mode === 'timed');
    setTimeRemaining(mode === 'timed' ? 600 : 0);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setQuestionsAttempted(0);
  };

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer !== null) {
      setQuestionsAttempted(prev => prev + 1);
      if (selectedAnswer === sampleQuestions?.[currentQuestion]?.correctAnswer) {
        setScore(prev => prev + 1);
      }
    }
    setShowExplanation(true);
  };

  const handleNextQuestion = () => {
    if (currentQuestion < sampleQuestions?.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      // End of assessment
      setIsTimerActive(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Beginner': 'text-green-600 bg-green-50',
      'Intermediate': 'text-blue-600 bg-blue-50',
      'Advanced': 'text-yellow-600 bg-yellow-50',
      'Expert': 'text-purple-600 bg-purple-50'
    };
    return colors?.[difficulty] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-8">
      {/* Assessment Mode Selection */}
      {!isTimerActive && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {assessmentModes?.map((mode) => (
            <div
              key={mode?.id}
              onClick={() => setAssessmentMode(mode?.id)}
              className={`
                p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 hover:shadow-md
                ${assessmentMode === mode?.id
                  ? 'border-indigo-500 bg-indigo-50 shadow-md'
                  : 'border-gray-200 hover:border-indigo-300'
                }
              `}
            >
              <div className="flex items-center space-x-3 mb-2">
                <span className="text-2xl">{mode?.icon}</span>
                <h3 className="font-semibold text-gray-800">{mode?.name}</h3>
              </div>
              <p className="text-sm text-gray-600">{mode?.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Category Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories?.map((category) => (
          <div
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`
              p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg
              ${selectedCategory === category?.id
                ? 'border-indigo-500 bg-indigo-50 shadow-md transform scale-105'
                : 'border-gray-200 bg-white hover:border-indigo-300'
              }
            `}
          >
            <div className="flex items-center space-x-3 mb-4">
              <span className="text-3xl">{category?.icon}</span>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{category?.name}</h3>
                <p className="text-sm text-gray-500">{category?.description}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Questions:</span>
                <span className="font-medium">{category?.questions}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Difficulty:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(category?.difficulty)}`}>
                  {category?.difficulty}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Avg. Time:</span>
                <span className="text-sm font-medium">{category?.avgTime}</span>
              </div>
            </div>

            {selectedCategory === category?.id && !isTimerActive && (
              <div className="mt-4 space-y-2">
                <Button 
                  variant="default" 
                  size="sm" 
                  fullWidth
                  onClick={() => startAssessment('timed')}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Start Timed Assessment
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  fullWidth
                  onClick={() => startAssessment('practice')}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Practice Mode
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Question Interface */}
      {(isTimerActive || questionsAttempted > 0) && (
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Header with Timer and Progress */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Question {currentQuestion + 1} of {sampleQuestions?.length}
              </div>
              <div className="text-sm text-gray-600">
                Score: {score}/{questionsAttempted}
              </div>
            </div>
            
            {assessmentMode === 'timed' && (
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <div className={`font-mono text-lg font-bold ${timeRemaining < 60 ? 'text-red-600' : 'text-blue-600'}`}>
                  {formatTime(timeRemaining)}
                </div>
                <button
                  onClick={() => setIsTimerActive(!isTimerActive)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  {isTimerActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
            <div 
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / sampleQuestions?.length) * 100}%` }}
            />
          </div>

          {/* Question Content */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(sampleQuestions?.[currentQuestion]?.difficulty)}`}>
                {sampleQuestions?.[currentQuestion]?.difficulty}
              </span>
              <div className="flex space-x-1">
                {sampleQuestions?.[currentQuestion]?.tags?.map((tag, index) => (
                  <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-6">
              {sampleQuestions?.[currentQuestion]?.question}
            </h3>

            <div className="space-y-3">
              {sampleQuestions?.[currentQuestion]?.options?.map((option, index) => (
                <label
                  key={index}
                  className={`
                    flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                    ${selectedAnswer === index
                      ? showExplanation
                        ? index === sampleQuestions?.[currentQuestion]?.correctAnswer
                          ? 'border-green-500 bg-green-50' :'border-red-500 bg-red-50' :'border-indigo-500 bg-indigo-50' :'border-gray-200 hover:border-gray-300'
                    }
                    ${showExplanation && index === sampleQuestions?.[currentQuestion]?.correctAnswer
                      ? 'border-green-500 bg-green-50' :''
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="answer"
                    value={index}
                    checked={selectedAnswer === index}
                    onChange={() => handleAnswerSelect(index)}
                    disabled={showExplanation}
                    className="w-4 h-4 text-indigo-600 mt-1"
                  />
                  <span className="text-gray-800 flex-1">{option}</span>
                  {showExplanation && index === sampleQuestions?.[currentQuestion]?.correctAnswer && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  {showExplanation && selectedAnswer === index && index !== sampleQuestions?.[currentQuestion]?.correctAnswer && (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Explanation */}
          {showExplanation && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-800 mb-2">Explanation:</h4>
                  <p className="text-blue-700">{sampleQuestions?.[currentQuestion]?.explanation}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              disabled={currentQuestion === 0}
              onClick={() => {
                setCurrentQuestion(prev => prev - 1);
                setSelectedAnswer(null);
                setShowExplanation(false);
              }}
            >
              Previous
            </Button>
            
            {!showExplanation ? (
              <Button 
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null}
              >
                Submit Answer
              </Button>
            ) : (
              <Button onClick={handleNextQuestion}>
                {currentQuestion === sampleQuestions?.length - 1 ? 'Finish Assessment' : 'Next Question'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Performance Summary */}
      {questionsAttempted > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Current Performance</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {questionsAttempted > 0 ? Math.round((score / questionsAttempted) * 100) : 0}%
              </div>
              <div className="text-sm text-green-700">Accuracy</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{questionsAttempted}</div>
              <div className="text-sm text-blue-700">Questions Answered</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">2.8min</div>
              <div className="text-sm text-purple-700">Avg. Time</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{score}</div>
              <div className="text-sm text-orange-700">Correct Answers</div>
            </div>
          </div>

          {simulatorState?.weakAreas && (
            <div>
              <h4 className="font-semibold mb-3">Areas for Improvement:</h4>
              <div className="flex flex-wrap gap-2">
                {simulatorState?.weakAreas?.map((area, index) => (
                  <span key={index} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MCQAssessment;